/**
 * P6 — N2 · Competência 5 (Probabilidade e combinatória) · task-model H2.20.09
 *
 *   "Contar as diagonais de um polígono, reconhecendo-as como combinações de 2."
 *
 * Estreia o **estímulo manipulável**: um controle deslizante muda o número de lados e
 * o aluno vê as diagonais aparecerem.
 *
 * DECISÃO QUE DEFINE O ITEM: o polígono da pergunta tem MAIS lados do que o controle
 * alcança. Se o aluno pudesse arrastar até o valor perguntado, bastaria contar as
 * linhas na tela — e o item deixaria de medir combinatória para medir paciência. Com
 * o controle limitado a casos pequenos, a manipulação faz o que deve fazer: mostrar
 * que de cada vértice saem n−3 diagonais e que cada uma é contada duas vezes. A
 * generalização continua sendo trabalho do aluno.
 *
 * É a diferença entre manipulação como FERRAMENTA DE PENSAMENTO e manipulação como
 * resposta — a fronteira entre N2 e N3.
 */

import { formatBR } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import { assembleSelectOne } from '../../engine/assemble.ts';
import { comExplicacoes, type Explicacoes } from '../../engine/explain.ts';
import { poligonoComDiagonais } from '../../render/chart.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  CNT_ARRANJO,
  CNT_CONTA_LADOS,
  CNT_DOBRO_LADOS,
  CNT_ESQUECE_O_PROPRIO,
  CNT_NAO_SUBTRAI_LADOS,
  CNT_SEM_DIVIDIR,
  CNT_SUBTRAI_LADOS_DUAS_VEZES,
  CNT_UM_VERTICE,
} from '../../schema/errors.ts';
import type { BuggySolver, ItemTemplate } from '../../schema/item.ts';
import { NOT_APPLICABLE, templateId } from '../../schema/item.ts';
import { br, strong, txt } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

interface Cenario {
  readonly objeto: string;
  readonly contexto: string;
}

/**
 * CONTEXTOS DE OBJETOS QUE O ALUNO CONHECE.
 *
 * Uma versão anterior falava em "cobertura tensionada" e "viga sob o deque". Contexto
 * de item de avaliação não é lugar de vocabulário técnico: quando o aluno precisa
 * primeiro decifrar o cenário, o item passa a medir leitura de engenharia, não
 * combinatória — e mede pior, porque quem não conhece a palavra trava antes de chegar
 * à matemática. Praça, mesa, jardim e painel todo mundo já viu.
 */
const CENARIOS: readonly Cenario[] = [
  {
    objeto: 'uma praça',
    contexto:
      'Uma praça tem a forma de um polígono regular de {n} lados. A prefeitura vai abrir um caminho reto ligando cada par de cantos que NÃO são vizinhos.',
  },
  {
    objeto: 'um painel',
    contexto:
      'Um painel decorativo tem a forma de um polígono regular de {n} lados. O artesão vai esticar um cordão entre cada par de cantos que NÃO são vizinhos.',
  },
  {
    objeto: 'uma mesa',
    contexto:
      'O tampo de uma mesa tem a forma de um polígono regular de {n} lados. Serão coladas fitas ligando cada par de cantos que NÃO são vizinhos.',
  },
  {
    objeto: 'um jardim',
    contexto:
      'Um jardim tem a forma de um polígono regular de {n} lados. Serão abertas trilhas ligando cada par de cantos que NÃO são vizinhos.',
  },
];

/** Alcance do controle deslizante — deliberadamente abaixo do polígono perguntado. */
const EXPLORACAO_MIN = 4;
const EXPLORACAO_MAX = 9;

export interface Params {
  readonly cenario: Cenario;
  /** Número de lados do polígono da PERGUNTA. */
  readonly n: number;
}

/**
 * `n` fica sempre acima do teto do controle, com folga: contar 54 diagonais na tela
 * não é uma alternativa prática à fórmula, e é isso que preserva o construto.
 */
function drawParams(rng: Rng): Params {
  return {
    cenario: rng.pick(CENARIOS),
    n: rng.pick([12, 14, 15, 16, 18, 20]),
  };
}

/** A chave: n(n − 3)/2. */
const solveKey = (p: Params): number => (p.n * (p.n - 3)) / 2;

const BUGGY: readonly BuggySolver<Params, number>[] = [
  {
    // O erro que dá nome ao task-model: conta TODOS os segmentos entre vértices,
    // incluindo os lados.
    error: CNT_NAO_SUBTRAI_LADOS,
    priority: 1,
    solve: (p) => (p.n * (p.n - 1)) / 2,
  },

  // --- faixa 2: sorteados entre si -----------------------------------------
  {
    error: CNT_SEM_DIVIDIR,
    priority: 2,
    solve: (p) => p.n * (p.n - 3),
  },
  {
    error: CNT_ARRANJO,
    priority: 2,
    solve: (p) => p.n * (p.n - 1),
  },
  {
    error: CNT_UM_VERTICE,
    priority: 2,
    solve: (p) => p.n - 3,
  },
  {
    error: CNT_CONTA_LADOS,
    priority: 2,
    solve: (p) => p.n,
  },
  {
    // Descontou os dois vizinhos e esqueceu que o próprio vértice também não conta.
    // Fica logo ACIMA da chave — é o distrator mais próximo do conjunto.
    //
    // SÓ COM n PAR. Com n ímpar, n(n − 2)/2 não é inteiro: com n = 15 dá 97,5, que o
    // formato de zero casas arredondava para 98. O aluno via "98" entre as
    // alternativas e lia, na explicação, "Você fez 15 × 13 ÷ 2 = 98" — um item
    // afirmando uma conta falsa. O erro que este distrator representa continua
    // existindo com n ímpar; o que não existe é um número inteiro para nomeá-lo.
    error: CNT_ESQUECE_O_PROPRIO,
    priority: 2,
    solve: (p) => (p.n % 2 === 0 ? (p.n * (p.n - 2)) / 2 : NOT_APPLICABLE),
  },
  {
    // Descontou os n lados duas vezes. Fica ABAIXO da chave.
    error: CNT_SUBTRAI_LADOS_DUAS_VEZES,
    priority: 2,
    solve: (p) => {
      const v = (p.n * (p.n - 1)) / 2 - 2 * p.n;
      return v > 0 ? v : NOT_APPLICABLE;
    },
  },

  // --- faixa 3: reposição ---------------------------------------------------
  {
    error: CNT_DOBRO_LADOS,
    priority: 3,
    solve: (p) => (p.n * 2 === p.n - 3 ? NOT_APPLICABLE : p.n * 2),
  },
];

// ---------------------------------------------------------------------------
// Explicações: a conta de cada erro, com os números desta variante
// ---------------------------------------------------------------------------

const n0 = (x: number): string => formatBR(x, 0);

const EXPLICA: Explicacoes<Params, number> = {
  [CNT_NAO_SUBTRAI_LADOS]: (p, v) => [
    txt(
      `Você contou todos os segmentos que ligam dois vértices: C(${n0(p.n)}, 2) = ` +
        `${n0(p.n)} × ${n0(p.n - 1)} ÷ 2 = ${n0(v)}. `,
    ),
    txt(
      `Mas ${n0(p.n)} desses segmentos são os lados do polígono, e lado não é diagonal. ` +
        `Faltou tirar os lados: ${n0(v)} − ${n0(p.n)} = ${n0(solveKey(p))}.`,
    ),
  ],
  [CNT_SEM_DIVIDIR]: (p, v) => [
    txt(
      `Você fez ${n0(p.n)} × ${n0(p.n - 3)} = ${n0(v)}: de cada um dos ${n0(p.n)} vértices ` +
        `saem ${n0(p.n - 3)} diagonais. `,
    ),
    txt(
      'Até aí, certo. Mas cada diagonal liga dois vértices, e assim ela foi contada duas ' +
        'vezes — uma em cada ponta. Faltou dividir por 2.',
    ),
  ],
  [CNT_ARRANJO]: (p, v) => [
    txt(
      `Você fez ${n0(p.n)} × ${n0(p.n - 1)} = ${n0(v)}. Essa conta trata AB e BA como ` +
        'ligações diferentes e ainda inclui os lados. ',
    ),
    txt(
      `Uma diagonal não tem ida e volta — AB e BA são a mesma —, e os ${n0(p.n)} lados ` +
        'não são diagonais.',
    ),
  ],
  [CNT_UM_VERTICE]: (p, v) => [
    txt(`${n0(p.n)} − 3 = ${n0(v)} é o número de diagonais que saem de UM vértice. `),
    txt(`O polígono tem ${n0(p.n)} vértices, e a pergunta é pelo total de diagonais do polígono.`),
  ],
  [CNT_CONTA_LADOS]: (_p, v) => [
    txt(
      `${n0(v)} é o número de lados do polígono. Os lados ligam vértices vizinhos; ` +
        'as diagonais ligam vértices que NÃO são vizinhos.',
    ),
  ],
  [CNT_ESQUECE_O_PROPRIO]: (p, v) => [
    txt(
      `Você fez ${n0(p.n)} × ${n0(p.n - 2)} ÷ 2 = ${n0(v)}: de cada vértice, descontou os ` +
        'dois vizinhos. ',
    ),
    txt(
      `Mas o próprio vértice também não forma diagonal consigo mesmo. De cada vértice saem ` +
        `${n0(p.n)} − 3 = ${n0(p.n - 3)} diagonais, e não ${n0(p.n - 2)}.`,
    ),
  ],
  [CNT_SUBTRAI_LADOS_DUAS_VEZES]: (p, v) => {
    const c = (p.n * (p.n - 1)) / 2;
    return [
      txt(
        `Você fez C(${n0(p.n)}, 2) − 2 × ${n0(p.n)} = ${n0(c)} − ${n0(2 * p.n)} = ${n0(v)}, ` +
          'tirando os lados duas vezes. ',
      ),
      txt(`Cada lado aparece uma única vez entre os ${n0(c)} segmentos: basta tirar ${n0(p.n)}.`),
    ];
  },
  [CNT_DOBRO_LADOS]: (p, v) => [
    txt(
      `Você fez 2 × ${n0(p.n)} = ${n0(v)}. Essa conta não vem da estrutura do problema: ` +
        'o número de diagonais cresce muito mais depressa que o número de lados.',
    ),
  ],
};

export const diagonaisPoligono: ItemTemplate<Params, number> = {
  id: templateId('h2-20-09-diagonais'),
  version: 2,
  taskModel: taskModelCode('H2.20.09'),
  skills: [skillCode('H2.20')],
  axis: 'II',
  level: 'N2',
  title: 'Diagonais de um polígono',
  responseModel: '3PL',

  difficultySources: ['steps.2', 'combined-skill'],

  params: drawParams,
  solve: solveKey,
  buggy: comExplicacoes(BUGGY, EXPLICA),

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // Quanto maior o polígono, mais longe fica o alcance do controle — e mais o item
    // depende da fórmula em vez da contagem.
    if (p.n >= 16) fontes.push('steps.3plus');
    return fontes;
  },

  build(p, key, distractors, rng) {
    const interaction = assembleSelectOne<number>({
      structure: 'E1',
      key,
      distractors,
      render: (v) => [txt(formatBR(v, 0))],
      raw: (v) => v,
      rng,
    });

    const enunciado = p.cenario.contexto.replace('{n}', formatBR(p.n, 0));

    return {
      // A instrução de exploração vem ANTES do applet: instrução que aparece depois do
      // recurso chega tarde — o aluno já mexeu sem saber o que procurar.
      stem: [
        txt(enunciado),
        br,
        // Molde do autor (2026-09-29) para os itens com explorador.
        txt(
          'Utilize o explorador abaixo para contar, em polígonos com diferentes números de lados, quantas diagonais partem de ',
        ),
        strong('cada'),
        txt(' vértice. Repare que cada diagonal é contada '),
        strong('duas vezes'),
        txt(' quando se consideram os dois vértices que ela liga. Procure identificar um padrão.'),
      ],
      comando: [
        txt('Quantas dessas ligações — as '),
        strong('diagonais'),
        txt(` do polígono de ${formatBR(p.n, 0)} lados — existem ao todo?`),
      ],
      stimuli: [
        {
          kind: 'manipulable',
          motion: 'interactive',
          // Sem áudio: aplicável numa sala de aula comum.
          audio: { need: 'none' },
          id: 'explorador',
          alt:
            'Explorador interativo: um polígono regular com todas as suas diagonais traçadas. ' +
            `Um controle permite variar o número de lados entre ${EXPLORACAO_MIN} e ${EXPLORACAO_MAX} ` +
            'para observar como as diagonais se multiplicam. O polígono do enunciado, com ' +
            `${formatBR(p.n, 0)} lados, está fora do alcance do controle.`,
          controls: [
            {
              id: 'lados',
              label: [txt('Lados do polígono')],
              min: EXPLORACAO_MIN,
              max: EXPLORACAO_MAX,
              step: 1,
              initial: 5,
              format: (v) =>
                `${formatBR(v, 0)} lados → ${formatBR((v * (v - 3)) / 2, 0)} diagonais`,
            },
          ],
          frame: (params) => {
            const k = Math.round(params['lados'] ?? 5);
            return poligonoComDiagonais(k, {
              title: `Polígono regular de ${k} lados com suas diagonais`,
              desc:
                `Polígono regular de ${k} lados. Todas as diagonais estão traçadas: ` +
                `de cada vértice partem ${k - 3}, e o total é ${(k * (k - 3)) / 2}.`,
            });
          },
        },
      ],
      interaction,
      dicas: [
        [
          txt(
            'Use o explorador: num polígono pequeno, conte quantas diagonais saem de um único vértice.',
          ),
        ],
        [
          txt(
            `De cada vértice saem diagonais para todos os outros, menos para ele mesmo e para os dois vizinhos. ` +
              `Quantas saem de cada vértice do polígono de ${formatBR(p.n, 0)} lados?`,
          ),
        ],
        [
          txt(
            `Multiplicar esse número pelos ${formatBR(p.n, 0)} vértices conta cada diagonal duas vezes — uma vez em cada ponta.`,
          ),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(
          `De cada um dos ${formatBR(p.n, 0)} vértices partem ${formatBR(p.n - 3, 0)} diagonais ` +
            '— todos os outros vértices, menos os dois vizinhos e o próprio.',
        ),
        br,
        txt(
          `Isso dá ${formatBR(p.n, 0)} × ${formatBR(p.n - 3, 0)} = ` +
            `${formatBR(p.n * (p.n - 3), 0)}, mas cada diagonal foi contada duas vezes, ` +
            'uma por extremidade.',
        ),
        br,
        txt(`Total = ${formatBR(p.n * (p.n - 3), 0)} ÷ 2 = ${formatBR(key, 0)} diagonais.`),
        br,
        txt(
          `Contar C(${formatBR(p.n, 0)}, 2) = ${formatBR((p.n * (p.n - 1)) / 2, 0)} incluiria ` +
            `os ${formatBR(p.n, 0)} lados, que não são diagonais.`,
        ),
      ],
      declared: p.n >= 16 ? 'dificil' : 'media',
    };
  },

  validate(_built, p) {
    if (p.n <= EXPLORACAO_MAX) {
      return `n = ${p.n} está dentro do alcance do controle: o aluno poderia contar em vez de calcular`;
    }
    return null;
  },
};
